import type { QuestionChatCostCategory } from "@/client";

/** Where each bar starts, as the running total of the percentages before it. */
export const getCategoryBarOffsets = (
	categories: QuestionChatCostCategory[],
) => {
	let runningPercent = 0;

	return categories.map((category) => {
		const offset = runningPercent;
		runningPercent += category.percent ?? 0;
		return offset;
	});
};

export const isIncludedCategory = (category: QuestionChatCostCategory) =>
	(category.credits ?? 0) === 0 && (category.operations ?? 0) > 0;

export const formatCredits = (credits: number) =>
	Math.round(credits).toLocaleString(undefined, {
		maximumFractionDigits: 0,
	});

export const formatRefreshedAt = (
	timestamp: number | undefined,
	now: number,
) => {
	if (timestamp === undefined) {
		return "never";
	}

	const seconds = Math.max(0, Math.round((now - timestamp) / 1000));

	if (seconds < 5) {
		return "just now";
	}
	if (seconds < 60) {
		return `${seconds}s ago`;
	}

	const minutes = Math.floor(seconds / 60);
	if (minutes < 60) {
		return `${minutes}m ago`;
	}

	return `${Math.floor(minutes / 60)}h ago`;
};

export const REFRESHED_AT_TICK_MS = 5000;

export const formatTimeWorked = (seconds: number | null | undefined) => {
	if (seconds === null || seconds === undefined) {
		return "—";
	}
	if (seconds < 60) {
		return `${seconds.toFixed(1)}s`;
	}
	const minutes = Math.floor(seconds / 60);
	const remainingSeconds = seconds % 60;
	return `${minutes}m ${remainingSeconds.toFixed(0)}s`;
};

export type AccountUsageCategory = {
	label: string;
	symbol: string;
	credits: number;
};

export const formatCreditsWithUnit = (credits: number) =>
	`${formatCredits(credits)} cr`;

/** Bar width as a percentage of the largest category in the set. */
export const accountUsageBarPercent = (
	credits: number,
	categories: AccountUsageCategory[],
) => {
	const max = Math.max(...categories.map((category) => category.credits), 0);
	if (max <= 0) {
		return 0;
	}
	return (credits / max) * 100;
};
